import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_UTIL from '../../services/AuthIntereptor';
import { toast } from 'react-toastify';
import Webcam from 'react-webcam';
import { Button, Input, ModalShell, Icons } from '../ui';
import logofull from '../../media/images/logofull.png';
import EditPortfolioModal from './EditPortfolioModal';

const AppBar = ({ showLogout = true, showCoins = false }) => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [portraitModalOpen, setPortraitModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const webcamRef = useRef(null);

  const popularDomains = ['@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@aol.com'];

  useEffect(() => {
    const fetchUserDetails = async () => {
      const userPhoneNumber = localStorage.getItem('userPhoneNumber');
      if (!userPhoneNumber) {
        navigate('/login');
        return;
      }

      try {
        const response = await API_UTIL.get(`/getUserDetails/${userPhoneNumber}`);
        const userData = response.data;
        setUserDetails(userData);

        // Check for portrait first (higher priority)
        if (!userData.potrait_s3_url) {
          setPortraitModalOpen(true);
        } else {
          // Store portrait URL if not already in localStorage
          if (!localStorage.getItem('potrait_s3_url')) {
            localStorage.setItem('potrait_s3_url', userData.potrait_s3_url);
          }

          // If portrait exists but email is missing, open email modal
          if (!userData.email) {
            setEmailModalOpen(true);
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        toast.error('Failed to load user data');
      }
    };

    fetchUserDetails();
  }, [navigate]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }

    const userPhoneNumber = localStorage.getItem('userPhoneNumber');

    try {
      const response = await API_UTIL.post('/updateUserEmail', {
        userPhoneNumber,
        email,
      });

      if (response.status === 200) {
        setUserDetails({ ...userDetails, email });
        setEmailModalOpen(false);
        toast.success('Email updated successfully');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error('Failed to update email');
    }
  };

  const handleEmailInput = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address (e.g., user@example.com)');
    } else {
      setEmailError('');
    }
  };

  const emailOptions = popularDomains
    .map((domain) => {
      const username = email.split('@')[0] || '';
      return email.includes('@') ? '' : `${username}${domain}`;
    })
    .filter((option) => option);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setImgSrc(imageSrc);
  };

  const retake = () => {
    setImgSrc(null);
  };

  const handlePortraitUpload = async () => {
    if (!termsAccepted) {
      toast.error('Please accept terms and conditions');
      return;
    }

    const userPhoneNumber = localStorage.getItem('userPhoneNumber');
    const formData = new FormData();
    formData.append('image', imgSrc);
    formData.append('username', userPhoneNumber);
    formData.append('event', ''); // Empty event for AppBar uploads

    setIsSubmitting(true);

    try {
      const uploadResponse = await API_UTIL.post(`/uploadUserPotrait`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (uploadResponse.data && uploadResponse.data.potrait_s3_url) {
        localStorage.setItem('potrait_s3_url', uploadResponse.data.potrait_s3_url);
        setUserDetails({ ...userDetails, potrait_s3_url: uploadResponse.data.potrait_s3_url });
      }

      setPortraitModalOpen(false);
      toast.success('Selfie uploaded successfully!');
      setImgSrc(null);

      if (!userDetails?.email) {
        setEmailModalOpen(true);
      }
    } catch (error) {
      console.error('Error uploading portrait:', error);
      toast.error('Failed to upload selfie. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const videoConstraints = {
    width: 350,
    height: 250,
    facingMode: 'user',
  };

  const phone = userDetails?.user_phone_number || localStorage.getItem('userPhoneNumber') || '';
  const displayName = userDetails?.user_name && userDetails.user_name !== phone ? userDetails.user_name : '';
  const initials = displayName
    ? displayName.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '';
  const showProfile = showLogout && !!phone;

  return (
    <>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200 relative">
        <div className="w-full px-3 sm:px-5 h-16 flex items-center justify-between sm:justify-center">

          {/* Center — logo */}
          <button onClick={() => navigate('/Event')} className="flex shrink-0 items-center" aria-label="Home">
            <img src={logofull} alt="ImageShield" className="h-[42px] w-auto sm:h-12" />
          </button>

          {/* Right — profile */}
          <div className="relative shrink-0 sm:absolute sm:right-5 sm:top-1/2 sm:-translate-y-1/2">
            {showProfile ? (
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex max-w-[calc(100vw-8rem)] items-center gap-1.5 rounded-full border border-gray-200 py-1 pl-1 pr-1.5 transition hover:border-gray-300 hover:bg-gray-50 sm:max-w-none sm:gap-2 sm:pr-2"
              >
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-600 text-[11px] font-semibold text-white sm:h-7 sm:w-7 sm:text-xs">
                  {initials || <Icons.User className="w-4 h-4" />}
                </span>
                <span className="block max-w-[112px] truncate text-xs font-medium text-slate-700 sm:max-w-none sm:text-sm">
                  {phone}
                </span>
                <Icons.Chevron className="h-3.5 w-3.5 shrink-0 text-slate-400 sm:h-4 sm:w-4" />
              </button>
            ) : (
              <span className="text-xs text-slate-400">Not signed in</span>
            )}

            {menuOpen && showProfile && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-card-hover p-1 scale-in z-30">
                  <div className="px-3 py-2 text-xs text-slate-500">Signed in as</div>
                  <div className="px-3 pb-2 text-sm font-medium text-slate-900 truncate">{phone}</div>
                  <div className="h-px bg-gray-100 my-1" />
                  <button
                    onClick={() => { setMenuOpen(false); setPortfolioOpen(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-gray-50 rounded-lg"
                  >
                    <Icons.Edit className="w-4 h-4" />
                    Edit Portfolio
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-gray-50 rounded-lg"
                  >
                    <Icons.Logout className="w-4 h-4" />
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      </header>

      {/* Portrait (selfie) modal — required, not dismissible */}
      <ModalShell open={portraitModalOpen} onClose={() => {}} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-lg font-bold text-slate-900">Selfie Required</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Perfect lighting, perfect you — let's snap that selfie! :)
          </p>
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="captured"
              className="w-full max-w-[350px] mx-auto mb-4 block rounded-xl shadow-card"
            />
          ) : (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              mirrored={true}
              className="w-full max-w-[350px] mx-auto mb-4 block rounded-xl shadow-card"
            />
          )}
          {imgSrc ? (
            <div className="space-y-2">
              <Button
                className="w-full"
                onClick={handlePortraitUpload}
                disabled={!termsAccepted || isSubmitting}
              >
                {isSubmitting ? 'Submitting…' : 'Submit Photo'}
              </Button>
              <Button variant="secondary" className="w-full" onClick={retake}>
                Retake photo
              </Button>
              <label className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-4 h-4 accent-brand-600"
                />
                <span>
                  Accept{' '}
                  <a
                    href="/TermsAndConditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline"
                  >
                    terms and conditions
                  </a>
                </span>
              </label>
            </div>
          ) : (
            <Button className="w-full" onClick={capture}>
              Capture photo
            </Button>
          )}
        </div>
      </ModalShell>

      {/* Email capture modal */}
      <ModalShell open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-lg font-bold text-slate-900">Stay Updated</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Share your email to receive your photos if your phone number isn't supported.
          </p>
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <Input
              label="Email"
              type="email"
              required
              value={email}
              onChange={handleEmailInput}
              list="appbar-email-suggestions"
              placeholder="you@example.com"
              error={emailError}
            />
            <datalist id="appbar-email-suggestions">
              {emailOptions.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
            <Button type="submit" className="w-full" disabled={!!emailError}>
              Submit
            </Button>
          </form>
        </div>
      </ModalShell>

      <EditPortfolioModal
        open={portfolioOpen}
        onClose={() => setPortfolioOpen(false)}
        userPhoneNumber={phone}
        onSaved={(updatedUser) => {
          if (updatedUser) setUserDetails((current) => ({ ...current, ...updatedUser }));
        }}
      />
    </>
  );
};

export default AppBar;
